"use client";

import React, { useState, useEffect, useRef } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  FormControl,
  FormLabel,
  Input,
  VStack,
  Text,
  Box,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  useToast,
} from "@chakra-ui/react";
import { ChevronDownIcon } from "@chakra-ui/icons";
import type { PronunciationEntry } from "./PronunciationDictionary";

interface PronunciationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: PronunciationEntry) => Promise<void>;
  initialData?: PronunciationEntry | null;
}

interface CustomSelectProps {
  label: string;
  value: string;
  placeholder: string;
  options: Array<{ value: string; label: string }>;
  onSelect: (value: string) => void;
  isDisabled?: boolean;
}

// CustomSelect (styled to match dashboard components)
const CustomSelect: React.FC<CustomSelectProps> = ({
  label,
  value,
  placeholder,
  options,
  onSelect,
  isDisabled = false,
}) => {
  const selectedOption = options.find((opt) => opt.value === value);
  const buttonRef = useRef<HTMLButtonElement>(null);

  return (
    <Box>
      <Text mb={2} fontWeight="medium" color="gray.800">
        {label}
      </Text>
      <Menu>
        <MenuButton
          as={Button}
          ref={buttonRef}
          rightIcon={<ChevronDownIcon />}
          isDisabled={isDisabled}
          bg="white"
          color="gray.800"
          borderColor="gray.300"
          borderWidth="1px"
          _hover={{ bg: "white" }}
          _active={{ bg: "white" }}
          _expanded={{ bg: "white" }}
          w="100%"
          textAlign="left"
          fontSize={14}
        >
          {selectedOption ? selectedOption.label : placeholder}
        </MenuButton>
        <MenuList
          bg="white"
          borderColor="gray.300"
          minW={
            buttonRef.current ? `${buttonRef.current.offsetWidth}px` : "100%"
          }
          fontSize={14}
        >
          <MenuItem
            onClick={() => onSelect("")}
            bg="white"
            color="gray.800"
            _hover={{ bg: "gray.100" }}
          >
            {placeholder}
          </MenuItem>
          {options.map((option) => (
            <MenuItem
              key={option.value}
              onClick={() => onSelect(option.value)}
              bg="white"
              color="gray.800"
              _hover={{ bg: "gray.100" }}
            >
              {option.label}
            </MenuItem>
          ))}
        </MenuList>
      </Menu>
    </Box>
  );
};

export const PronunciationModal: React.FC<PronunciationModalProps> = ({
  isOpen,
  onClose,
  onSave,
  initialData,
}) => {
  const [word, setWord] = useState("");
  const [alphabet, setAlphabet] = useState<"ipa" | "cmu">("cmu");
  const [phoneme, setPhoneme] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const toast = useToast();

  useEffect(() => {
    if (initialData) {
      setWord(initialData.word);
      setAlphabet(initialData.alphabet);
      setPhoneme(initialData.phoneme);
    } else {
      setWord("");
      setAlphabet("cmu");
      setPhoneme("");
    }
  }, [initialData]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await onSave({ word, alphabet, phoneme });
      toast({
        title: "Pronunciation saved",
        status: "success",
        duration: 2000,
      });
    } catch (error: any) {
      toast({
        title: "Error saving pronunciation",
        description:
          error?.response?.data?.message || "An unexpected error occurred",
        status: "error",
        duration: 3000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const alphabetOptions = [
    { value: "cmu", label: "CMU" },
    { value: "ipa", label: "IPA" },
  ];

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <ModalOverlay />
      <ModalContent bg="white" color="gray.800">
        <form onSubmit={handleSubmit}>
          <ModalHeader color="gray.800">
            {initialData ? "Edit Pronunciation" : "Add Pronunciation"}
          </ModalHeader>
          <ModalBody>
            <VStack spacing={4}>
              <FormControl isRequired>
                <FormLabel>Word</FormLabel>
                <Input
                  id="pronunciation-word"
                  value={word}
                  onChange={(e) => setWord(e.target.value)}
                  placeholder="Enter word or phrase"
                  _placeholder={{ color: "gray.600" }}
                  bg="white"
                  borderColor="gray.300"
                  _hover={{ borderColor: "gray.400" }}
                  _focus={{
                    borderColor: "primary.500",
                    boxShadow: "0 0 0 1px var(--chakra-colors-primary-500)",
                  }}
                />
              </FormControl>

              <FormControl isRequired>
                <CustomSelect
                  label="Pronunciation"
                  value={alphabet}
                  placeholder="Select alphabet"
                  options={alphabetOptions}
                  onSelect={(val) =>
                    setAlphabet((val as "ipa" | "cmu") || "cmu")
                  }
                  isDisabled={isSubmitting}
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Phoneme</FormLabel>
                <Input
                  id="pronunciation-phoneme"
                  value={phoneme}
                  onChange={(e) => setPhoneme(e.target.value)}
                  placeholder="Enter phonetic pronunciation"
                  _placeholder={{ color: "gray.600" }}
                  bg="white"
                  borderColor="gray.300"
                  _hover={{ borderColor: "gray.400" }}
                  _focus={{
                    borderColor: "primary.500",
                    boxShadow: "0 0 0 1px var(--chakra-colors-primary-500)",
                  }}
                />
              </FormControl>
            </VStack>
          </ModalBody>

          <ModalFooter>
            <Button
              variant="ghost"
              mr={3}
              onClick={onClose}
              color="gray.700"
              _hover={{ bg: "gray.100" }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              colorScheme="primary"
              isLoading={isSubmitting}
            >
              Save
            </Button>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  );
};
